import type { ReactNode } from "react";
import { ErrorBanner } from "./ErrorBanner.js";
import { PrimaryButton } from "./FormField.js";
import {
  priorityBadgeClassName,
  priorityBadgeLabel,
  priorityBadgeTitle,
  type PriorityBadge,
} from "../lib/priority-badges.js";

export interface PriorityListItem {
  modelId: string;
  name: string;
  /** 平台编辑器保留第二行 mono id；云端卡不传。 */
  subtitle?: string;
  badge: PriorityBadge;
  availability: string;
  /** 给了 = 这一行的模型已下线（N-88）：名字划线 + 琥珀小徽标，文案即本值。 */
  retiredLabel?: string;
}

export interface PriorityListOption {
  modelId: string;
  label: string;
}

export interface PriorityListEditorProps {
  title: string;
  blurb: string;
  headerExtra?: ReactNode;
  /** 列表顶部（说明之下、条目之上）的提示条。 */
  notice?: ReactNode;
  items: PriorityListItem[];
  options: PriorityListOption[];
  onMove: (index: number, delta: -1 | 1) => void;
  onRemove: (index: number) => void;
  onAdd: (modelId: string) => void;
  onSave: () => void;
  saving: boolean;
  savedAt?: string | null;
  error?: string | null;
  footerNote?: ReactNode;
  children?: ReactNode;
  addDisabled?: boolean;
  saveDisabled?: boolean;
  addLabel: string;
  addPlaceholder?: string;
  saveLabel: string;
  emptyLabel?: string;
  idleNote?: string;
  savedNote?: string;
  ariaPrefix: string;
  testId: string;
}

export function PriorityListEditor(props: PriorityListEditorProps): JSX.Element {
  const placeholder = props.addPlaceholder ?? props.addLabel;
  const lastIndex = props.items.length - 1;
  const statusText = props.savedNote
    ?? (props.savedAt ? "已保存 " + props.savedAt : props.idleNote ?? "");

  return (
    <section data-testid={props.testId} className="space-y-3 rounded-lg border border-dosia-border bg-dosia-bg-light p-4">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold text-dosia-text">{props.title}</h2>
          {props.headerExtra}
        </div>
        <p className="mt-1 text-xs text-dosia-text-secondary">{props.blurb}</p>
      </div>
      {props.error ? <ErrorBanner>{props.error}</ErrorBanner> : null}
      {props.notice}
      <div className="space-y-2">
        {props.items.length === 0 && (
          <div className="text-sm text-dosia-text-secondary">{props.emptyLabel ?? "尚未配置默认主脑顺序。"}</div>
        )}
        {props.items.map((item, index) => (
          <div
            key={item.modelId}
            data-testid={"priority-item-" + item.modelId}
            className="flex flex-wrap items-center gap-2 rounded border border-dosia-border bg-dosia-bg px-3 py-2"
          >
            <span className="w-7 text-sm text-dosia-text-secondary">{index + 1}</span>
            <div className="min-w-[220px] flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={item.retiredLabel ? "font-medium text-dosia-text-secondary line-through" : "font-medium text-dosia-text"}>
                  {item.name}
                </span>
                {item.retiredLabel ? (
                  <span className="rounded border border-dosia-warning/40 bg-dosia-warning/10 px-1.5 py-0.5 text-[11px] text-dosia-warning">
                    {item.retiredLabel}
                  </span>
                ) : null}
              </div>
              {item.subtitle ? (
                <div className="font-mono text-xs text-dosia-text-secondary">{item.subtitle}</div>
              ) : null}
            </div>
            <span className={`rounded bg-dosia-accent/15 px-2 py-0.5 text-xs ${priorityBadgeClassName(item.badge)}`} title={priorityBadgeTitle(item.badge)}>
              {priorityBadgeLabel(item.badge)}
            </span>
            <span className="rounded border border-dosia-border px-2 py-0.5 text-xs text-dosia-text-secondary">
              {item.availability}
            </span>
            <div className="flex items-center gap-1">
              <button type="button" className="rounded px-2 py-1 text-dosia-text-secondary hover:bg-dosia-bg-lighter disabled:opacity-40" disabled={index === 0} onClick={() => props.onMove(index, -1)} aria-label={props.ariaPrefix + "上移-" + index}>↑</button>
              <button type="button" className="rounded px-2 py-1 text-dosia-text-secondary hover:bg-dosia-bg-lighter disabled:opacity-40" disabled={index === lastIndex} onClick={() => props.onMove(index, 1)} aria-label={props.ariaPrefix + "下移-" + index}>↓</button>
              <button type="button" className="rounded px-2 py-1 text-dosia-text-secondary hover:bg-dosia-bg-lighter" onClick={() => props.onRemove(index)} aria-label={props.ariaPrefix + "删除-" + index}>✕</button>
            </div>
          </div>
        ))}
      </div>
      {props.footerNote ? (
        <p className="text-xs text-dosia-text-secondary">{props.footerNote}</p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="min-w-[280px] rounded border border-dosia-border bg-dosia-bg px-2 py-1.5 text-sm text-dosia-text"
          aria-label={props.addLabel}
          defaultValue=""
          disabled={props.addDisabled}
          onChange={(event) => {
            const value = event.target.value;
            event.currentTarget.value = "";
            if (!value) return;
            props.onAdd(value);
          }}
        >
          <option value="">{placeholder}</option>
          {props.options.map((option) => (
            <option key={option.modelId} value={option.modelId}>
              {option.label}
            </option>
          ))}
        </select>
        <PrimaryButton onClick={props.onSave} disabled={props.saving || props.saveDisabled}>
          {props.saving ? "保存中…" : props.saveLabel}
        </PrimaryButton>
        <span className="text-xs text-dosia-text-secondary">{statusText}</span>
      </div>
      {props.children}
    </section>
  );
}
